/** Un proyecto: su estado, sus tareas y su historial. */
import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { ErrorDeApi, api } from '../api'
import { Cargando, Error, EtiquetaDeEstado, EtiquetaDePrioridad, Vacio } from '../componentes/piezas'
import { usePortal } from '../contexto'
import { ESTADO_PROYECTO, ESTADO_TAREA, fecha, fechaYHora } from '../textos'
import type { DetalleDeProyecto, EstadoProyecto } from '../tipos'

export default function Proyecto() {
  const { numero = '' } = useParams()
  const { ruta, servidor, puedo, avisar } = usePortal()
  const [datos, setDatos] = useState<DetalleDeProyecto | null>(null)
  const [error, setError] = useState('')

  const cargar = useCallback(async () => {
    try {
      setDatos(await api.get<DetalleDeProyecto>(`${ruta}/proyectos/${numero}`))
      setError('')
    } catch (e) {
      setError((e as ErrorDeApi).message)
    }
  }, [ruta, numero])

  useEffect(() => {
    setDatos(null)
    void cargar()
  }, [cargar])

  async function cambiarEstado(estado: EstadoProyecto) {
    try {
      await api.patch(`${ruta}/proyectos/${numero}`, { estado })
      avisar(`Proyecto ${ESTADO_PROYECTO[estado].toLowerCase()}.`, 'bien')
      void cargar()
    } catch (e) {
      avisar((e as ErrorDeApi).message, 'malo')
    }
  }

  if (error) {
    return (
      <div className="contenido">
        <Error>{error}</Error>
        <Link className="boton" to={`/${servidor.id}/proyectos`}>Volver a proyectos</Link>
      </div>
    )
  }
  if (!datos) return <div className="contenido"><Cargando filas={5} /></div>

  const { proyecto, tareas, historial } = datos
  const porcentaje = proyecto.tareas > 0
    ? Math.round((proyecto.tareas_hechas / proyecto.tareas) * 100)
    : 0

  return (
    <>
      <header className="cabecera">
        <Link className="boton boton--plano" to={`/${servidor.id}/proyectos`} aria-label="Volver">
          ←
        </Link>
        <div>
          <div className="codigo">{proyecto.codigo}</div>
          <h1>{proyecto.nombre}</h1>
        </div>
        <div className="cabecera__acciones">
          <EtiquetaDeEstado texto={proyecto.estado_texto} />
        </div>
      </header>

      <div className="contenido">
        <div className="detalle">
          <div>
            {puedo('gestionar_proyectos') && (
              <div className="tarjeta" style={{ marginBottom: 16 }}>
                <div className="tarjeta__cuerpo fila-botones">
                  {(Object.keys(ESTADO_PROYECTO) as EstadoProyecto[])
                    .filter((e) => e !== proyecto.estado)
                    .map((e) => (
                      <button key={e} className="boton" onClick={() => void cambiarEstado(e)}>
                        {ESTADO_PROYECTO[e]}
                      </button>
                    ))}
                </div>
              </div>
            )}

            {proyecto.descripcion && (
              <div className="tarjeta" style={{ marginBottom: 16 }}>
                <div className="tarjeta__cuerpo texto-largo">{proyecto.descripcion}</div>
              </div>
            )}

            <section className="tarjeta">
              <div className="tarjeta__titulo">
                Tareas · {proyecto.tareas_hechas} de {proyecto.tareas} hechas ({porcentaje} %)
              </div>
              {tareas.length === 0 ? (
                <Vacio titulo="Este proyecto no tiene tareas" />
              ) : (
                <ul className="lista">
                  {tareas.map((t) => (
                    <li key={t.numero} className="fila">
                      <EtiquetaDePrioridad valor={t.prioridad} />
                      <div className="fila__principal">
                        <div className="fila__titulo">{t.titulo}</div>
                        <div className="fila__meta">
                          <span className="codigo">{t.codigo}</span>
                          {t.responsable && <span>{t.responsable.nombre}</span>}
                          {t.vence && (
                            <span style={{ color: t.vencida ? 'var(--malo)' : undefined }}>
                              {t.vencida ? 'venció' : 'vence'} {fecha(t.vence)}
                            </span>
                          )}
                        </div>
                      </div>
                      <span className="etiqueta">{ESTADO_TAREA[t.estado]}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>

          <aside>
            <div className="tarjeta">
              <div className="tarjeta__titulo">Datos</div>
              <div className="tarjeta__cuerpo" style={{ paddingTop: 4, paddingBottom: 4 }}>
                <div className="dato">
                  <span className="dato__nombre">Prioridad</span>
                  <span className="dato__valor">
                    <EtiquetaDePrioridad valor={proyecto.prioridad} />
                  </span>
                </div>
                <div className="dato">
                  <span className="dato__nombre">Responsable</span>
                  <span className="dato__valor">{proyecto.responsable?.nombre ?? 'Sin asignar'}</span>
                </div>
                <div className="dato">
                  <span className="dato__nombre">Empezó</span>
                  <span className="dato__valor">{fecha(proyecto.inicio)}</span>
                </div>
                <div className="dato">
                  <span className="dato__nombre">Estimación</span>
                  <span className="dato__valor">
                    {proyecto.estimada ? fecha(proyecto.estimada) : 'Sin estimar'}
                  </span>
                </div>
                <div className="dato">
                  <span className="dato__nombre">Comprometido</span>
                  <span className="dato__valor">
                    {proyecto.comprometida ? fecha(proyecto.comprometida) : 'Ninguna fecha'}
                  </span>
                </div>
                {proyecto.solicitud && (
                  <div className="dato">
                    <span className="dato__nombre">Nació de</span>
                    <span className="dato__valor">{proyecto.solicitud}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="tarjeta" style={{ marginTop: 14 }}>
              <div className="tarjeta__titulo">Historial</div>
              <div className="tarjeta__cuerpo">
                {historial.length === 0 ? (
                  <span style={{ color: 'var(--texto-tenue)' }}>Nada todavía.</span>
                ) : (
                  <ul className="historial">
                    {historial.map((l) => (
                      <li key={l.id}>
                        {l.frase}
                        <time dateTime={l.cuando}>{fechaYHora(l.cuando)}</time>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </>
  )
}
